import styled from "@emotion/styled"
import BusinessIcon from "@mui/icons-material/Business"
import RoomIcon from "@mui/icons-material/Room"
import { Dialog, Grid, Link, Typography } from "@mui/material"
import * as api from "api"
import { ServiceCenterApplicationType } from "api"
import CloseButton from "common/CloseButton"
import PhoneIcon from "common/icons/PhoneIcon"
import { useState } from "react"
import Shared from "theme/shared"
import ContactForm from "../forms/ContactForm"
import FormResponse from "../forms/FormResponse"
import TreatmentForm from "../forms/TreatmentForm"

const Styled = {
  Wrapper: styled.div`
    padding: 15px;
    text-align: center;
  `,
  TitleWrap: styled.div`
    text-align: center;
    padding-bottom: 15px;
    border-bottom: 1px solid ${(p) => p.theme.colors.silver};
    margin-bottom: 30px;
  `,
  Title: styled(Typography)<{ component: string }>``,
  Grid: styled(Grid)`
    justify-content: center;
    flex-wrap: wrap;
    gap: 10px;
    display: flex;
  `,
  Property: styled(Typography)`
    font-size: 1rem;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    margin-bottom: 0.5em;
    justify-content: center;

    ${(p) => p.theme.breakpoints.up("md")} {
      font-size: 1.25rem;
    }

    svg {
      font-size: 1.2em;
      color: ${(p) => p.theme.colors.bluishGrey};

      margin-inline-end: 0.3em;
      g {
        fill: ${(p) => p.theme.colors.bluishGrey};
      }
    }
    span {
      width: 100%;
    }
  `,
  Link: styled(Link)`
    color: ${(p) => p.theme.colors.marine} !important;
    text-decoration: underline;
    display: flex;
  `,
  ResponseWrapper: styled.div`
    text-align: center;
    padding: 30px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
  `,
  ResponseIcon: styled.img`
    width: 71px;
    height: 71px;
    object-fit: contain;
  `,
  ResponseText: styled(Typography)`
    font-weight: bold;
    font-size: 20px;
  `,
  ResponseButton: styled(Shared.Button)`
    height: 35px;
    font-size: 14px;
    width: auto;
    width: 150px;
    white-space: nowrap;
    margin-top: 16px;
  `,
}

export type ServiceCenterContactStatus = "form" | "success" | "failure"

type ServiceCenterContactDialogProps = {
  onClose: () => void
  serviceCenter: api.SingleServiceCenter & api.ServiceCenter
  type?: ServiceCenterApplicationType
}

const ServiceCenterContactDialog = ({
  onClose,
  serviceCenter,
  type,
}: ServiceCenterContactDialogProps) => {
  const [status, setStatus] = useState<ServiceCenterContactStatus>("form")

  const handleOnClose = () => {
    setStatus("form")
    onClose()
  }

  return (
    <Dialog
      open={type != null}
      onClose={handleOnClose}
      aria-labelledby="dialog-title"
      maxWidth="sm"
      scroll="body"
    >
      <CloseButton onClick={handleOnClose} />
      <Styled.Wrapper>
        {status === "form" && (
          <>
            <Styled.TitleWrap>
              <Styled.Title id="dialog-title" variant="h1" component="h2">
                {serviceCenter.name}
              </Styled.Title>
              <Styled.Grid>
                <Styled.Property style={{ width: "100%" }}>
                  <RoomIcon />
                  {serviceCenter.address}
                </Styled.Property>

                <Styled.Property title="טלפון">
                  <PhoneIcon />
                  <Styled.Link href={`tel:${serviceCenter.phone}`}>
                    {serviceCenter.phone}
                  </Styled.Link>
                </Styled.Property>

                {serviceCenter.fax && (
                  <Styled.Property title="פקס">
                    <BusinessIcon />
                    {serviceCenter.fax}
                  </Styled.Property>
                )}
              </Styled.Grid>
            </Styled.TitleWrap>

            {type === "Contact" && (
              <ContactForm
                ServiceCenter={serviceCenter}
                onChange={(status) => setStatus(status)}
              />
            )}
            {type === "Treatment" && (
              <TreatmentForm
                ServiceCenter={serviceCenter}
                onChange={(status) => setStatus(status)}
              />
            )}
          </>
        )}
        {status !== "form" && (
          <FormResponse
            type={type}
            status={status}
            onActionClick={handleOnClose}
          />
        )}
      </Styled.Wrapper>
    </Dialog>
  )
}

export default ServiceCenterContactDialog
